import { z } from "zod";
import { optionalPhoneBRSchema } from "../common/phone";
import { cuidSchema } from "../common/id";

export const APPOINTMENT_STATUSES = [
  "SCHEDULED",
  "CONFIRMED",
  "WAITING",
  "IN_PROGRESS",
  "COMPLETED",
  "LATE",
  "CANCELLED",
  "NO_SHOW",
] as const;

export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

/** Consulta que CONTA — a que aconteceu, está acontecendo ou ainda vai acontecer. Cancelada e
 *  falta ficam de fora: entram na contagem, viram volume que não existiu. É a mesma régua usada
 *  pelo painel do Início e pela Equipe, e por isso mora aqui e não em cada tela. */
export const APPOINTMENT_ACTIVE_STATUSES = [
  "SCHEDULED",
  "CONFIRMED",
  "WAITING",
  "IN_PROGRESS",
  "COMPLETED",
  "LATE",
] as const satisfies readonly AppointmentStatus[];

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  SCHEDULED: "Agendado",
  CONFIRMED: "Confirmado",
  WAITING: "Em espera",
  IN_PROGRESS: "Em atendimento",
  COMPLETED: "Atendido",
  LATE: "Atrasado",
  CANCELLED: "Cancelado",
  NO_SHOW: "Faltou",
};

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  SCHEDULED: "#3B82F6",
  CONFIRMED: "#10B981",
  WAITING: "#14B8A6",
  IN_PROGRESS: "#06B6D4",
  COMPLETED: "#6B7280",
  LATE: "#F97316",
  CANCELLED: "#EF4444",
  NO_SHOW: "#F59E0B",
};

/** HH:MM (24h) — valida formato. */
export const timeStringSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Formato invalido (HH:MM)");

export const cancelledBySchema = z.enum(["professional", "patient"]);

/** Cria novo appointment. patientId OU patientName obrigatorio. */
export const appointmentCreateSchema = z
  .object({
    patientId: cuidSchema.optional(),
    patientName: z.string().trim().min(2).max(120),
    patientPhone: optionalPhoneBRSchema,
    dentistId: cuidSchema.optional(),
    dentistName: z.string().trim().max(120).optional(),
    salaId: z.string().min(1).nullable().optional(),
    /** Serviços escolhidos, na ordem de seleção — a cor do card vem do primeiro e a duração
     *  sugerida é a MAIOR entre eles (procedimentos rodam em paralelo na mesma cadeira).
     *  String livre e não cuid: os serviços convertidos de procedureTypes pela migration de
     *  unificação nasceram com UUID (SQL não gera cuid). Quem valida é o servidor: o FK sozinho
     *  aceitaria procedimento de outra organização. */
    serviceIds: z.array(z.string().min(1)).max(20).default([]),
    procedure: z
      .string()
      .trim()
      .max(300)
      .optional()
      .transform((v) => v ?? ""),
    date: z.coerce.date(),
    startTime: timeStringSchema,
    endTime: timeStringSchema,
    status: appointmentStatusSchema.default("SCHEDULED"),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    notes: z.string().trim().max(2000).optional(),
    sendReminder: z.boolean().default(false),
  })
  .refine((data) => data.startTime < data.endTime, {
    message: "startTime deve ser anterior a endTime",
    path: ["endTime"],
  });

export const appointmentUpdateSchema = z
  .object({
    patientId: cuidSchema.nullable().optional(),
    patientName: z.string().trim().min(2).max(120).optional(),
    patientPhone: optionalPhoneBRSchema,
    dentistId: cuidSchema.nullable().optional(),
    dentistName: z.string().trim().max(120).nullable().optional(),
    salaId: z.string().min(1).nullable().optional(),
    /** Ausente = não mexe nos vínculos; array (inclusive vazio) = substitui o conjunto inteiro. */
    serviceIds: z.array(z.string().min(1)).max(20).optional(),
    procedure: z.string().trim().max(300).optional(),
    date: z.coerce.date().optional(),
    startTime: timeStringSchema.optional(),
    endTime: timeStringSchema.optional(),
    status: appointmentStatusSchema.optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    sendReminder: z.boolean().optional(),
    cancelledBy: cancelledBySchema.nullable().optional(),
  })
  .refine(
    (data) =>
      !data.startTime || !data.endTime || data.startTime < data.endTime,
    { message: "startTime deve ser anterior a endTime", path: ["endTime"] },
  );

/** Span máximo de um range de agenda: 366 dias cobre qualquer visão de calendário (dia/semana/mês,
 *  e até um ano inteiro) com folga pra ano bissexto. Existe porque a query de `listByRange` não tem
 *  `take`: sem limite de span, "me dá de 1900 a 2100" devolve a agenda inteira da organização com
 *  três joins por linha, e o custo cai no Postgres compartilhado com os outros tenants. */
const RANGE_MAX_DIAS = 366;

/** Filtro de listagem pra calendar views (dia/semana/mes). */
export const appointmentListRangeSchema = z
  .object({
    rangeStart: z.coerce.date(),
    rangeEnd: z.coerce.date(),
    dentistId: cuidSchema.optional(),
  })
  .refine((d) => d.rangeEnd >= d.rangeStart, {
    message: "rangeEnd deve ser posterior a rangeStart",
    path: ["rangeEnd"],
  })
  .refine(
    (d) => (d.rangeEnd.getTime() - d.rangeStart.getTime()) / 86_400_000 <= RANGE_MAX_DIAS,
    { message: `Período muito longo (máximo ${RANGE_MAX_DIAS} dias)`, path: ["rangeEnd"] },
  );

export type AppointmentCreateInput = z.infer<typeof appointmentCreateSchema>;
export type AppointmentUpdateInput = z.infer<typeof appointmentUpdateSchema>;
export type AppointmentListRangeInput = z.infer<typeof appointmentListRangeSchema>;
